/**
 * 用户反馈管理 Composable
 * 负责点赞、点踩、原因选择等反馈相关功能
 */

import { ref, h } from "vue";
import { ElMessage } from "element-plus/es/components/message";
import { ElMessageBox } from "element-plus/es/components/message-box";

export function useMessageFeedback() {
  /**
   * 点踩原因选项
   */
  const DISLIKE_REASONS = [
    { label: "回答不准确", value: "inaccurate" },
    { label: "回答不完整", value: "incomplete" },
    { label: "回答不相关", value: "irrelevant" },
    { label: "语气不当", value: "tone" },
    { label: "格式混乱", value: "format" },
    { label: "其他原因", value: "other" },
  ];

  /**
   * 显示点踩原因选择对话框
   * @returns 选中的原因数组
   */
  const showDislikeReasonDialog = (): Promise<string[]> => {
    return new Promise((resolve) => {
      let selectedReasons: string[] = [];

      ElMessageBox({
        title: "为什么不满意？",
        message: h("div", null, [
          h(
            "p",
            { style: "margin-bottom: 12px; color: #606266;" },
            "请选择不满意的原因（可多选）：",
          ),
          h(
            "div",
            { style: "display: flex; flex-direction: column; gap: 8px;" },
            DISLIKE_REASONS.map((reason) =>
              h(
                "label",
                {
                  style:
                    "display: flex; align-items: center; gap: 8px; cursor: pointer; padding: 4px;",
                  onMouseenter: (e: MouseEvent) => {
                    (e.currentTarget as HTMLElement).style.background =
                      "#f5f7fa";
                  },
                  onMouseleave: (e: MouseEvent) => {
                    (e.currentTarget as HTMLElement).style.background =
                      "transparent";
                  },
                },
                [
                  h("input", {
                    type: "checkbox",
                    value: reason.value,
                    onChange: (e: Event) => {
                      const target = e.target as HTMLInputElement;
                      if (target.checked) {
                        selectedReasons.push(reason.value);
                      } else {
                        selectedReasons = selectedReasons.filter(
                          (r) => r !== reason.value,
                        );
                      }
                    },
                    style: "width: 16px; height: 16px; cursor: pointer;",
                  }),
                  h("span", { style: "color: #303133;" }, reason.label),
                ],
              ),
            ),
          ),
        ]),
        showCancelButton: true,
        confirmButtonText: "提交",
        cancelButtonText: "取消",
        distinguishCancelAndClose: true,
        beforeClose: (action, instance, done) => {
          if (action === "confirm") {
            if (selectedReasons.length === 0) {
              ElMessage.warning("请至少选择一个原因");
              return;
            }
            resolve(selectedReasons);
            done();
          } else {
            resolve([]);
            done();
          }
        },
      });
    });
  };

  /**
   * 切换点赞状态（已点赞则取消，未点赞则点赞）
   */
  const toggleLike = async (
    messageId: string,
    currentUserId: string,
    messages: any[],
  ) => {
    console.log("点赞", messageId, currentUserId, messages);
    const message = messages.find((m) => (m as any).id === messageId);
    const currentFeedback = (message as any)?.feedbackType;

    if (currentFeedback === "like") {
      // 取消点赞
      await removeFeedback(messageId, currentUserId, messages);
    } else {
      // 点赞
      await handleLike(messageId, currentUserId, messages);
    }
  };

  /**
   * 切换点踩状态（已点踩则取消，未点踩则点踩）
   */
  const toggleDislike = async (
    messageId: string,
    currentUserId: string,
    messages: any[],
  ) => {
    const message = messages.find((m) => (m as any).id === messageId);
    const currentFeedback = (message as any)?.feedbackType;

    if (currentFeedback === "dislike") {
      // 取消点踩
      await removeFeedback(messageId, currentUserId, messages);
    } else {
      // 点踩（显示原因选择）
      await handleDislike(messageId, currentUserId, messages);
    }
  };

  /**
   * 处理点赞
   */
  const handleLike = async (
    messageId: string,
    currentUserId: string,
    messages: any[],
  ) => {
    try {
      // 🔗调用接口: 反馈数据入库
      await $fetch("/api/feedback/feedback", {
        method: "POST",
        body: {
          messageId,
          type: "like",
          userId: currentUserId,
        },
      });

      // ✅ 更新前端消息对象的反馈状态
      const message = messages.find((m) => (m as any).id === messageId);
      if (message) {
        (message as any).feedbackType = "like";
        (message as any).feedbackReasons = [];
      }

      ElMessage.success("感谢你的点赞！👍");
    } catch (error) {
      console.error("❌ 提交点赞失败:", error);
      ElMessage.error("点赞提交失败");
    }
  };

  /**
   * 处理点踩 - 显示原因选择对话框
   */
  const handleDislike = async (
    messageId: string,
    currentUserId: string,
    messages: any[],
  ) => {
    try {
      // 显示原因选择对话框
      const selectedReasons = await showDislikeReasonDialog();

      if (!selectedReasons || selectedReasons.length === 0) {
        return; // 用户取消了选择
      }

      // 🔗调用接口: 反馈数据入库
      await $fetch("/api/feedback/feedback", {
        method: "POST",
        body: {
          messageId,
          type: "dislike",
          reasonTags: selectedReasons,
          userId: currentUserId,
        },
      });

      // ✅ 更新前端消息对象的反馈状态
      const message = messages.find((m) => (m as any).id === messageId);
      if (message) {
        (message as any).feedbackType = "dislike";
        (message as any).feedbackReasons = selectedReasons;
      }

      ElMessage.success("感谢你的反馈，我们会继续改进！💪");
    } catch (error) {
      console.error("❌ 提交点踩失败:", error);
      ElMessage.error("反馈提交失败");
    }
  };

  /**
   * 移除反馈（取消点赞/点踩）
   */
  const removeFeedback = async (messageId: string, currentUserId: string, messages: any[]) => {
    try {
      // ✅ 调用后端删除接口（改用 POST 方法）
      await $fetch("/api/feedback/feedback-delete", {
        method: "POST",
        body: {
          messageId,
          userId: currentUserId,
        },
      });

      // 更新前端状态
      const message = messages.find((m) => (m as any).id === messageId);
      if (message) {
        (message as any).feedbackType = null;
        (message as any).feedbackReasons = [];
      }

      ElMessage.info("已取消反馈");
    } catch (error) {
      console.error("❌ 取消反馈失败:", error);
      ElMessage.error("取消反馈失败");
    }
  };

  /**
   * 加载消息的反馈状态（单条查询） -- 废弃，推荐使用批量加载方法 loadBatchMessageFeedbacks()
   */
  const loadMessageFeedback = async (
    messageId: string,
    currentUserId: string,
    messages: any[],
  ) => {
    try {
      const response: any = await $fetch("/api/feedback/feedback", {
        query: {
          messageId,
          userId: currentUserId,
        },
      });

      if (response.success && response.feedback) {
        const message = messages.find((m) => m.id === messageId);
        if (message) {
          (message as any).feedbackType = response.feedback.type;
          (message as any).feedbackReasons = response.feedback.reasonTags
            ? JSON.parse(response.feedback.reasonTags)
            : [];
        }
      }
    } catch (error) {
      console.error("❌ 加载反馈状态失败:", error);
    }
  };

  /**
   * 批量加载多个消息的反馈状态（优化版，推荐使用）
   */
  const loadBatchMessageFeedbacks = async (
    messageIds: string[],
    currentUserId: string,
    messages: any[],
  ) => {
    if (messageIds.length === 0) return;

    try {
      const response: any = await $fetch("/api/feedback/batch", {
        query: {
          messageIds: JSON.stringify(messageIds),
          userId: currentUserId,
        },
      });

      if (response.success && response.feedbacks) {
        // 更新每条消息的反馈状态
        messageIds.forEach((messageId) => {
          const feedback = response.feedbacks[messageId];
          if (feedback) {
            const message = messages.find((m) => m.id === messageId);
            if (message) {
              message.feedbackType = feedback.type;
              message.feedbackReasons = feedback.reasonTags
                ? JSON.parse(feedback.reasonTags)
                : [];
            }
          }
        });
      }
    } catch (error) {
      console.error("❌ 批量加载反馈状态失败:", error);
    }
  };

  return {
    DISLIKE_REASONS,
    handleLike,
    handleDislike,
    toggleLike,
    toggleDislike,
    removeFeedback,
    loadMessageFeedback,
    loadBatchMessageFeedbacks,
    showDislikeReasonDialog,
  };
}
